import { Injectable, inject } from '@angular/core';
import {
  AbEntrySearchService,
  LeadSearchService,
} from '@maximizer/core/global-search/data-access';
import { isEmail } from '@maximizer/shared/util';
import { Observable, catchError, forkJoin, map, of, timeout } from 'rxjs';
import { EmailRecipient } from '../models/email-draft';
import { mergeRecipients } from '../utils/email-draft.util';

const SEARCH_LIMIT = 20;
const SEARCH_TIMEOUT_MS = 8_000;

@Injectable()
export class EmailRecipientSearchService {
  private readonly abEntries = inject(AbEntrySearchService);
  private readonly leads = inject(LeadSearchService);

  search(term: string): Observable<EmailRecipient[]> {
    const phrase = term.trim();
    if (phrase === '') {
      return of([]);
    }
    return forkJoin({
      abEntries: this.source(
        this.abEntries.search(phrase, true, SEARCH_LIMIT),
        (entry) =>
          toRecipient(
            entry.email,
            entry.name || entry.displayValue || entry.companyName,
          ),
      ),
      leads: this.source(
        this.leads.search(phrase, true, SEARCH_LIMIT),
        (lead) => toRecipient(lead.email, lead.name || lead.companyName),
      ),
    }).pipe(map(({ abEntries, leads }) => mergeRecipients(abEntries, leads)));
  }

  private source<T>(
    results: Observable<T[]>,
    mapItem: (item: T) => EmailRecipient | null,
  ): Observable<EmailRecipient[]> {
    return results.pipe(
      map((items) =>
        items
          .map(mapItem)
          .filter((item): item is EmailRecipient => item !== null),
      ),
      timeout({ first: SEARCH_TIMEOUT_MS, with: () => of([]) }),
      catchError(() => of([])),
    );
  }
}

function toRecipient(
  rawEmail: string | null | undefined,
  rawName: string | null | undefined,
): EmailRecipient | null {
  const email = (rawEmail ?? '').trim();
  if (!isEmail(email)) {
    return null;
  }
  return { email, name: (rawName ?? '').trim() };
}
